// utils/chatUtils.js

/* ============================================================
   UUID
============================================================ */
export const uuid = () =>
  Math.random().toString(36).slice(2) + Date.now();

/* ============================================================
   TEXT RENDER HELPERS
============================================================ */

export const renderSystemText = (txt = "") => {
  if (!txt) return "";

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return txt.replace(
    urlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer"
         style="color:#075e54;text-decoration:underline;">${url}</a>`
  );
};

export const renderTextWithLinks = (txt = "") => {
  if (!txt) return "";

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return txt.replace(
    urlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer"
         style="color:#075e54;text-decoration:underline;">${url}</a>`
  );
};

/* ============================================================
   CRYPTO HELPERS (E2EE)
   AES-256-GCM
============================================================ */

const AES_ALGO = "AES-GCM";
const IV_LENGTH = 12; // AES-GCM requires 12 bytes

/* ============================================================
   BASE64 UTILITIES
============================================================ */

export const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);

  let binary = "";

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
};

export const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const len = binary.length;

  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

/* ============================================================
   KEY STORAGE
============================================================ */

export const savePrivateKey = async (chatId, key) => {
  const exported = await crypto.subtle.exportKey("pkcs8", key);

  localStorage.setItem(
    `chat-private-${chatId}`,
    bufferToBase64(exported)
  );
};

export const loadPrivateKey = async (chatId) => {
  const stored = localStorage.getItem(`chat-private-${chatId}`);

  if (!stored) return null;

  return crypto.subtle.importKey(
    "pkcs8",
    base64ToBuffer(stored),
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    ["deriveKey"]
  );
};

/* ============================================================
   KEY GENERATION
============================================================ */

export const generateKeyPair = async () => {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    ["deriveKey"]
  );

  return keyPair;
};

export const exportPublicKey = async (publicKey) => {
  const exported = await crypto.subtle.exportKey(
    "spki",
    publicKey
  );

  return bufferToBase64(exported);
};

export const importPublicKey = async (publicKeyBase64) => {
  return crypto.subtle.importKey(
    "spki",
    base64ToBuffer(publicKeyBase64),
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    []
  );
};

/* ============================================================
   DERIVE SHARED KEY
============================================================ */

export const deriveSharedKey = async (
  privateKey,
  publicKey
) => {
  return crypto.subtle.deriveKey(
    {
      name: "ECDH",
      public: publicKey,
    },
    privateKey,
    {
      name: AES_ALGO,
      length: 256,
    },
    false,
    ["encrypt", "decrypt"]
  );
};

/* ============================================================
   ENCRYPT MESSAGE
============================================================ */

export const encryptMessage = async (
  plaintext,
  sharedKey
) => {
  const iv = crypto.getRandomValues(
    new Uint8Array(IV_LENGTH)
  );

  const encoded = new TextEncoder().encode(plaintext);

  const ciphertext = await crypto.subtle.encrypt(
    {
      name: AES_ALGO,
      iv,
    },
    sharedKey,
    encoded
  );

  return {
    ciphertext: bufferToBase64(ciphertext),
    iv: bufferToBase64(iv),
  };
};

/* ============================================================
   DECRYPT MESSAGE
============================================================ */

export const decryptMessage = async (
  ciphertext,
  iv,
  sharedKey
) => {
  try {
    const decrypted = await crypto.subtle.decrypt(
      {
        name: AES_ALGO,
        iv: new Uint8Array(base64ToBuffer(iv)),
      },
      sharedKey,
      new Uint8Array(base64ToBuffer(ciphertext))
    );

    return new TextDecoder().decode(decrypted);
  } catch (err) {
    console.error("❌ decryptMessage failed", {
      ciphertextLength: ciphertext?.length,
      ivLength: iv?.length,
      err,
    });

    return "[Unable to decrypt]";
  }
};